import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CreateCustomerInput, Customer } from "@/gql/graphql";
import {
  CHECK_CUSTOMER_EMAIL_EXISTS_QUERY,
  CREATE_CUSTOMER_MUTATION,
  CUSTOMER_QUERY,
  CUSTOMERS_QUERY,
} from "@/graphql/customers";
import { useSalonStore } from "@/lib/store/salon";

interface UseCustomerStepProps {
  prefilledCustomerId?: string;
  onCustomerChange?: (customerId: string, customerData: Customer) => void;
  readonly?: boolean;
}

export function useCustomerStep({
  prefilledCustomerId,
  onCustomerChange,
  readonly = false,
}: UseCustomerStepProps = {}) {
  const { currentSalon } = useSalonStore();

  // Customer state
  const [customerId, setCustomerId] = useState(prefilledCustomerId || "");
  const [customerInput, setCustomerInput] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Email validation state
  const [emailValidationState, setEmailValidationState] = useState<{
    isValidating: boolean;
    emailExists: boolean | null;
    validatedEmail: string;
    error: string | null;
  }>({
    isValidating: false,
    emailExists: null,
    validatedEmail: "",
    error: null,
  });
  const emailValidationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Customer search query
  const {
    data: customersData,
    loading: customersLoading,
    error: customersError,
  } = useQuery(CUSTOMERS_QUERY, {
    variables: {
      salonId: currentSalon?.id || "",
      filters: {
        search: customerInput.trim() || undefined,
        isActive: true,
      },
    },
    skip: !currentSalon?.id || !customerInput.trim(),
  });

  // Fetch prefilled customer details
  const {
    data: prefilledCustomerData,
    loading: prefilledCustomerLoading,
    error: prefilledCustomerError,
  } = useQuery(CUSTOMER_QUERY, {
    variables: {
      salonId: currentSalon?.id || "",
      customerId: prefilledCustomerId || "",
    },
    skip: !currentSalon?.id || !prefilledCustomerId,
  });

  // Create customer mutation
  const [createCustomer, { loading: createCustomerLoading, error: createCustomerError }] =
    useMutation(CREATE_CUSTOMER_MUTATION);

  // Email validation lazy query
  const [checkEmailExists] = useLazyQuery(CHECK_CUSTOMER_EMAIL_EXISTS_QUERY, {
    onCompleted: (data) => {
      setEmailValidationState((prev) => ({
        ...prev,
        isValidating: false,
        emailExists: data.checkCustomerEmailExists,
        error: null,
      }));
    },
    onError: (error) => {
      setEmailValidationState((prev) => ({
        ...prev,
        isValidating: false,
        error: error.message || "Failed to validate email",
      }));
    },
  });

  // Email validation functions
  const validateEmailDebounced = useCallback(
    (email: string) => {
      // Clear previous timeout
      if (emailValidationTimeoutRef.current) {
        clearTimeout(emailValidationTimeoutRef.current);
      }

      // Skip validation if email is empty, invalid, or we don't have a salon
      if (!email.trim() || !email.includes("@") || !currentSalon?.id) {
        setEmailValidationState({
          isValidating: false,
          emailExists: null,
          validatedEmail: "",
          error: null,
        });
        return;
      }

      // Set validating state
      setEmailValidationState((prev) => ({
        ...prev,
        isValidating: true,
        validatedEmail: email,
        error: null,
      }));

      // Debounce the validation
      emailValidationTimeoutRef.current = setTimeout(() => {
        checkEmailExists({
          variables: {
            salonId: currentSalon.id,
            email: email,
          },
        });
      }, 500);
    },
    [checkEmailExists, currentSalon?.id]
  );

  const handleEmailChange = useCallback(
    (email: string) => {
      setCustomerEmail(email);
      // Only validate if we don't have a selected customer (new customer creation)
      if (!selectedCustomer) {
        validateEmailDebounced(email);
      }
    },
    [selectedCustomer, validateEmailDebounced]
  );

  // Reset customer data
  const resetCustomer = () => {
    setCustomerId("");
    setCustomerInput("");
    setCustomerEmail("");
    setCustomerPhone("");
    setSelectedCustomer(null);
    setShowSuggestions(false);
    // Clear email validation
    if (emailValidationTimeoutRef.current) {
      clearTimeout(emailValidationTimeoutRef.current);
    }
    setEmailValidationState({
      isValidating: false,
      emailExists: null,
      validatedEmail: "",
      error: null,
    });
  };

  // Handle customer selection from suggestions
  const handleCustomerSelect = (customer: Customer) => {
    setCustomerId(customer.id);
    setSelectedCustomer(customer);
    setCustomerInput(`${customer.firstName} ${customer.lastName}`);
    setCustomerEmail(customer.email || "");
    setCustomerPhone(customer.primaryPhone?.phone || customer.phoneNumbers?.[0]?.phone || "");
    setShowSuggestions(false);
    // Clear email validation when customer is selected
    if (emailValidationTimeoutRef.current) {
      clearTimeout(emailValidationTimeoutRef.current);
    }
    setEmailValidationState({
      isValidating: false,
      emailExists: null,
      validatedEmail: "",
      error: null,
    });
    onCustomerChange?.(customer.id, customer);
  };

  // Handle customer input change
  const handleCustomerInputChange = (input: string) => {
    setCustomerInput(input);
    setCustomerId("");
    setSelectedCustomer(null);
    setCustomerEmail("");
    setCustomerPhone("");
    setShowSuggestions(true);
    // Clear email validation when switching to new customer
    if (emailValidationTimeoutRef.current) {
      clearTimeout(emailValidationTimeoutRef.current);
    }
    setEmailValidationState({
      isValidating: false,
      emailExists: null,
      validatedEmail: "",
      error: null,
    });
  };

  // Create new customer
  const handleCreateCustomer = async (): Promise<string> => {
    if (!customerInput.trim()) {
      throw new Error("Customer name is required");
    }

    if (!currentSalon?.id) {
      throw new Error("No salon selected");
    }

    const names = customerInput.trim().split(" ");
    const firstName = names[0];
    const lastName = names.length > 1 ? names.slice(1).join(" ") : "";

    const newCustomerInput: CreateCustomerInput = {
      firstName,
      lastName,
      email: customerEmail.trim() || undefined,
      phones: customerPhone.trim()
        ? [
            {
              phone: customerPhone.trim(),
              label: "Mobile",
              isPrimary: true,
            },
          ]
        : undefined,
      dateOfBirth: undefined,
      notes: "Created during appointment booking",
    };

    const customerResult = await createCustomer({
      variables: {
        salonId: currentSalon.id,
        input: newCustomerInput,
      },
    });

    const newCustomerId = customerResult.data?.createCustomer?.id;
    if (!newCustomerId) {
      throw new Error("Failed to create customer");
    }

    return newCustomerId;
  };

  // Validation
  const isValid = () => {
    // Must have either selected customer or customer input
    const hasCustomer = !!(customerId || customerInput.trim());

    if (!hasCustomer) {
      return false;
    }

    // If we have a selected customer (existing customer), validation passes
    if (selectedCustomer) {
      return true;
    }

    // For new customers, check email validation if email is provided
    if (customerEmail.trim()) {
      // Email is still being validated
      if (emailValidationState.isValidating) {
        return false;
      }

      // Email validation failed
      if (emailValidationState.error) {
        return false;
      }

      // Email already exists
      if (emailValidationState.emailExists === true) {
        return false;
      }

      // Email was validated but doesn't match current input (user changed email after validation)
      if (
        emailValidationState.validatedEmail &&
        emailValidationState.validatedEmail !== customerEmail.trim()
      ) {
        return false;
      }
    }

    return true;
  };

  // Get final customer ID (create if needed)
  const getFinalCustomerId = async (): Promise<string> => {
    if (customerId) {
      return customerId;
    }
    return handleCreateCustomer();
  };

  // Update when prefilled customer data is fetched
  useEffect(() => {
    if (prefilledCustomerData?.customer) {
      const customer = prefilledCustomerData.customer as Customer;
      setCustomerId(customer.id);
      setSelectedCustomer(customer);
      setCustomerInput(`${customer.firstName} ${customer.lastName}`);
      setCustomerEmail(customer.email || "");
      setCustomerPhone(customer.primaryPhone?.phone || customer.phoneNumbers?.[0]?.phone || "");
      onCustomerChange?.(customer.id, customer);
    }
  }, [prefilledCustomerData, onCustomerChange]);

  // Fallback: Update when prefilled ID changes (in case query fails)
  useEffect(() => {
    if (prefilledCustomerId && !prefilledCustomerData) {
      setCustomerId(prefilledCustomerId);
    }
  }, [prefilledCustomerId, prefilledCustomerData]);

  // Cleanup email validation timeout on unmount
  useEffect(() => {
    return () => {
      if (emailValidationTimeoutRef.current) {
        clearTimeout(emailValidationTimeoutRef.current);
      }
    };
  }, []);

  const loading = customersLoading || createCustomerLoading || prefilledCustomerLoading;
  const error = customersError || createCustomerError || prefilledCustomerError;

  return {
    // State
    customerId,
    setCustomerId,
    customerInput,
    setCustomerInput,
    customerEmail,
    setCustomerEmail: handleEmailChange,
    customerPhone,
    setCustomerPhone,
    showSuggestions,
    setShowSuggestions,
    selectedCustomer,
    setSelectedCustomer,
    inputRef,

    // Data
    customersData,

    // Actions
    handleCustomerSelect,
    handleCustomerInputChange,
    handleCreateCustomer,
    resetCustomer,
    getFinalCustomerId,

    // Validation
    isValid,
    emailValidationState,

    // Configuration
    readonly,

    // Status
    loading,
    error,
    customersLoading,
    createCustomerLoading,
    prefilledCustomerLoading,
  };
}
